import * as React from 'react';
import axios from 'utils/axios';
import { fDateSimple } from 'utils/formatTime';
import { Num, BotaoExportar, semAcento } from '../gps';
import { ModalHistorico } from './modal';

/* ============================================================================
   HistoricoGPS - a trilha de auditoria de qualquer registro do sistema.

   Aparece dentro de seis modais do visual novo (usuario, grupo, aplicativo, APK,
   regional, politica) e em telas antigas. Por isso foi reescrito NO LUGAR, com o
   mesmo nome e as mesmas props: todo mundo que ja usava ganha o visual novo sem
   uma linha de mudanca no chamador.

   -------------------------------------------------------- DEFEITOS CORRIGIDOS

   1. CABECALHO INVISIVEL: `backgroundColor: common.black` com
      `color: common.black` - texto preto sobre preto. Os titulos das colunas
      existiam e ninguem nunca leu.

   2. NAO RECARREGAVA. O `useEffect` tinha lista de dependencias VAZIA, mas usa
      `chave`. Em qualquer lugar onde a chave chega depois da montagem (ou muda,
      como ao trocar de dispositivo sem desmontar o componente) a tabela ficava
      com o historico do registro anterior. Agora as dependencias sao reais.

   3. DUAS CONSULTAS NO BANCO POR ABERTURA. A URL era montada com interpolacao
      direta, entao `acao` e `chaveSecundaria` ausentes chegavam ao backend como a
      STRING "undefined". Em historicos.service.ts:225 `if (chaveSecundaria)` da
      verdadeiro para "undefined", e o service roda uma consulta inteira a mais que
      nunca podia achar nada. Aqui a URL leva somente o que existe.

   4. `console.log('formattedList::', ...)` em cada carga.

   5. Sem tratamento de erro: o `.catch(err => {})` engolia a falha e a tela ficava
      dizendo "Nenhum historico encontrado" - que e a mensagem de lista vazia, nao
      de erro. Quem via isso concluia que o registro nao tinha historico.

   -------------------------------------------------------------- UMA DECISAO
   O backend devolve em ordem CRESCENTE de dataExecucao (o `order` do service), ou
   seja o mais antigo primeiro. Aqui a tabela abre com o MAIS RECENTE primeiro,
   porque a pergunta que traz alguem ao historico e quase sempre "o que mudou
   agora"; quem quer a linha do tempo tem o botao de inverter. A ordenacao e
   local: nao muda consulta nem paginacao do servidor.
   ============================================================================ */

export interface HistoricoProps {
  codigo: number;
  mensagem: string;
  dataExecucao: string;
  tipo: string;
  chave: string;
  acao: string;
  dataCampo?: string;
  campoDataLista?: Array<HistoricoCampoDataProps>;
  usuarioExecutante?: {
    codigo: number;
    email: string;
    nome: string;
    status: string | number;
    perfil: string | number;
  };
}

export interface HistoricoDataProps {
  content: Array<HistoricoProps>;
  count: number;
}

export interface HistoricoCampoDataProps {
  campo: string;
  antigo?: string;
  novo?: string;
  chaveSecundaria?: string;
}

interface Props {
  tipo: 'DISPOSITIVO' | 'ENTIDADE' | 'POLITICA' | 'USUARIO' | 'APLICATIVO' | 'APK' | 'REGIONAL' | 'TICKET';
  acao?: 'LOGIN' | 'CRIAR' | 'EDITAR' | 'DELETAR' | 'APROVAR' | 'BLOQUEAR' | 'NEGAR' | 'RESET' | 'REINICIADO';
  chave: string | number;
  title?: string | number;
  chaveSecundaria?: string | number;
  dataCampo?: string;
}

const LINHAS = [10, 25, 100];


/** Tom por acao. Cor aqui carrega significado: DELETAR nao e igual a CRIAR. */
function tomAcao(acao?: string) {
  const a = String(acao || '').toUpperCase();
  if (a === 'DELETAR' || a === 'BLOQUEAR' || a === 'NEGAR') return 'bg-danger-soft text-danger';
  if (a === 'CRIAR' || a === 'APROVAR') return 'bg-ok-soft text-ok';
  if (a === 'LOGIN') return 'bg-info-soft text-info';
  return 'bg-soft text-muted-foreground';
}

export function HistoricoGPS({ tipo, acao, chave, title, chaveSecundaria }: Props) {
  const [lista, setLista] = React.useState<HistoricoProps[]>([]);
  const [carregando, setCarregando] = React.useState(false);
  const [erro, setErro] = React.useState<string | null>(null);

  const [busca, setBusca] = React.useState('');
  const [pagina, setPagina] = React.useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = React.useState(10);
  const [recentesPrimeiro, setRecentesPrimeiro] = React.useState(true);

  const [aberto, setAberto] = React.useState(false);
  const [campoDataLista, setCampoDataLista] = React.useState<Array<HistoricoCampoDataProps>>([]);

  const carregar = React.useCallback(async () => {
    if (!chave) {
      setLista([]);
      return;
    }

    setCarregando(true);
    setErro(null);
    try {
      // Somente parametros que EXISTEM entram na URL. Com interpolacao direta,
      // `acao` ausente virava a string "undefined" e disparava consulta extra.
      const params = new URLSearchParams({ tipo, chave: String(chave) });
      if (acao) params.set('acao', acao);
      if (chaveSecundaria) params.set('chaveSecundaria', String(chaveSecundaria));

      const { data } = await axios.get<HistoricoDataProps>(`/historicos?${params.toString()}`);
      const conteudo = Array.isArray(data?.content) ? data.content : [];

      setLista(
        conteudo.map((item) => ({
          ...item,
          // `dataCampo` e JSON gravado como texto pelo backend. Uma linha com JSON
          // invalido nao pode derrubar a tabela inteira: cai para lista vazia.
          campoDataLista: seguroJson(item.dataCampo),
        }))
      );
      setPagina(0);
    } catch (e: any) {
      setLista([]);
      setErro(e?.response?.data?.message || 'Não foi possível carregar o histórico.');
    } finally {
      setCarregando(false);
    }
  }, [tipo, acao, chave, chaveSecundaria]);

  React.useEffect(() => {
    carregar();
  }, [carregar]);

  /* 🔴 DEFEITO CORRIGIDO (20/08/2026) - A TABELA MOSTRAVA O MAIS ANTIGO PRIMEIRO,
     com o botao dizendo "Mais recentes primeiro".

     Aqui havia `copia.reverse()`. Inverter uma lista so entrega "mais recente
     primeiro" se ela chegar em ordem CRESCENTE - e nao chega: o
     `historicos.service.ts` termina com

         result.sort((a, b) => dataB - dataA);  // ordem decrescente

     ou seja, a API ja devolve do mais novo para o mais antigo. O `reverse` desfazia
     isso, e o estado inicial (`recentesPrimeiro = true`) mostrava exatamente o
     contrario do que o rotulo prometia.

     Agora ORDENA pela data, em vez de inverter. A diferenca que importa: ordenar
     nao depende da ordem em que os dados chegam - se um dia o backend mudar (ou uma
     das duas consultas de `chaveSecundaria` entrar fora de ordem, que e o caso
     quando ha chave secundaria e os resultados sao concatenados), a tela continua
     certa.

     Data invalida vai para o FIM em vez de embaralhar a lista: `NaN` em comparador
     produz ordenacao indefinida, e uma linha com data quebrada nao deve mover as
     outras de lugar. */
  /* PESQUISA (10/09/2026, a pedido).

     O historico e a unica tela do sistema que nao tinha como procurar dentro do
     que ja esta na mao. Uma politica antiga chega com dezenas de linhas, todas
     com a mesma mensagem ("Edicao de politica."), e a pergunta que se faz aqui
     e sempre a mesma: "o que o FULANO mexeu?" ou "o que mudou naquele dia?".
     Sem busca, so rolando pagina a pagina.

     FILTRA NO CLIENTE, e de proposito: o backend ja devolve o historico INTEIRO
     do registro numa unica resposta (a paginacao daqui sempre foi local), entao
     mandar a busca para o servidor seria uma ida a rede para reduzir uma lista
     que ja esta na memoria - mais lento e sem nada a ganhar.

     Procura em TUDO que a linha mostra, inclusive no que esta escondido atras
     do "ver N": nome e e-mail de quem fez, acao, mensagem, data e os campos
     alterados com valor antigo e novo. Procurar por um valor que se lembra ter
     sido trocado ("2 -> 1") e um caminho legitimo, e ele nao aparece na tabela.

     Sem acento e sem caixa: quem digita "politica" tem que achar "Política". */
  const filtrada = React.useMemo(() => {
    const alvo = semAcento(busca.trim());
    if (!alvo) return lista;

    const termos = alvo.split(/\s+/);

    return lista.filter((h) => {
      const texto = semAcento(
        [
          h.usuarioExecutante?.nome,
          h.usuarioExecutante?.email,
          h.acao,
          h.mensagem,
          h.dataExecucao,
          ...(h.campoDataLista || []).flatMap((c) => [c.campo, c.antigo, c.novo]),
        ]
          .filter(Boolean)
          .join(' ')
      );

      /* Todos os termos, em qualquer ordem: "augusto 08/04" acha as edicoes
         daquele usuario naquele dia sem depender de como a linha foi montada. */
      return termos.every((t) => texto.includes(t));
    });
  }, [lista, busca]);

  const ordenada = React.useMemo(() => {
    const emMs = (valor?: string) => {
      const t = new Date(valor || '').getTime();
      return Number.isFinite(t) ? t : null;
    };

    return [...filtrada].sort((a, b) => {
      const ta = emMs(a.dataExecucao);
      const tb = emMs(b.dataExecucao);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return recentesPrimeiro ? tb - ta : ta - tb;
    });
  }, [filtrada, recentesPrimeiro]);

  React.useEffect(() => {
    setPagina(0);
  }, [busca]);

  const totalPaginas = Math.max(1, Math.ceil(ordenada.length / linhasPorPagina));
  const paginaAtual = Math.min(pagina, totalPaginas - 1);
  const visiveis = ordenada.slice(paginaAtual * linhasPorPagina, paginaAtual * linhasPorPagina + linhasPorPagina);

  return (
    <div className="w-full">
      {title && <h3 className="mb-2 text-center text-[12.5px] font-bold">{title}</h3>}

      <div className="mb-2 flex flex-wrap items-center gap-2">
        {/* Com filtro ativo o contador mostra OS DOIS numeros. So o filtrado
            faz a pessoa achar que o resto sumiu; so o total mente sobre o que
            esta na tela. */}
        <span className="text-[12px] text-muted-foreground">
          <Num className="font-semibold text-ink">{filtrada.length}</Num>
          {busca.trim() ? <> de <Num>{lista.length}</Num> registro(s)</> : ' registro(s)'}
        </span>

        <label className="relative flex items-center">
          <svg
            width="13"
            height="13"
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden
            className="pointer-events-none absolute left-2 text-faint"
          >
            <path
              d="M8 2.5a5.5 5.5 0 104 9.3l3.4 3.4"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Pesquisar no histórico"
            aria-label="Pesquisar no histórico"
            className="h-7 w-52 rounded-md border border-line bg-surface pl-7 pr-6 text-[11px] outline-none focus:border-primary"
          />
          {busca && (
            <button
              type="button"
              onClick={() => setBusca('')}
              aria-label="Limpar pesquisa"
              className="absolute right-1.5 text-[12px] text-faint hover:text-ink"
            >
              ✕
            </button>
          )}
        </label>

        <button
          type="button"
          onClick={() => setRecentesPrimeiro((v) => !v)}
          className="h-7 rounded-md border border-line px-2 text-[11px] font-semibold text-muted-foreground hover:text-ink"
          title="Inverte a ordem por data de execução"
        >
          {recentesPrimeiro ? '↓ Mais recentes primeiro' : '↑ Mais antigos primeiro'}
        </button>

        <button
          type="button"
          onClick={carregar}
          disabled={carregando}
          className="h-7 rounded-md border border-line px-2 text-[11px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
        >
          {carregando ? 'Carregando...' : '↻ Recarregar'}
        </button>

        {/* EXPORTAR O HISTORICO.

            Este componente e a aba "Historico" de TODOS os cadastros do sistema
            (dispositivo, grupo, politica, usuario, aplicativo, APK, regional), e
            nenhum deles tinha como levar o registro para fora - justo o dado que se
            precisa em auditoria, onde o destino e uma planilha ou um e-mail.

            Exporta a lista INTEIRA, nao a pagina: aqui o backend devolve tudo de uma
            vez (`GET /historicos?tipo&chave`) e a paginacao e local, entao nao ha
            nada a mais para buscar.

            AS ALTERACOES VIRAM UMA LINHA DE TEXTO ("Nome: A -> B; Status: 1 -> 0").
            O `dataCampo` e uma lista de objetos; em planilha, coluna com objeto
            dentro sai como "[object Object]". Uma coluna legivel vale mais que a
            estrutura preservada - quem precisa da estrutura tem a tela, que mostra
            antes/depois em tabela. */}
        <BotaoExportar
          className="h-7 px-2 text-[11px]"
          rotulo={`Excel (${ordenada.length})`}
          nomeArquivo={`historico-${String(tipo).toLowerCase()}-${String(chave || '')}`}
          linhas={() =>
            ordenada.map((h) => ({
              'Data de execução': h.dataExecucao || '',
              Ação: h.acao || '',
              Mensagem: h.mensagem || '',
              'Usuário': h.usuarioExecutante?.nome || '',
              'E-mail': h.usuarioExecutante?.email || '',
              Alterações: (h.campoDataLista || [])
                .map((c) => `${c.campo}: ${c.antigo ?? '—'} -> ${c.novo ?? '—'}`)
                .join('; '),
            }))
          }
        />

        <label className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground">
          Itens por página
          <select
            className="h-7 rounded-md border border-line bg-surface px-1.5 text-[11px]"
            value={linhasPorPagina}
            onChange={(e) => {
              setLinhasPorPagina(Number(e.target.value));
              setPagina(0);
            }}
          >
            {LINHAS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {erro && (
        <div className="rounded-lg border border-danger bg-danger-soft p-3 text-[12px]">
          {erro}{' '}
          <button type="button" onClick={carregar} className="font-bold underline">
            tentar de novo
          </button>
        </div>
      )}

      {!erro && (
        <div className="max-h-[60vh] overflow-auto rounded-lg border border-line">
          <table className="w-full border-collapse text-[12px]">
            <thead className="sticky top-0 bg-soft">
              <tr>
                <th className="px-3 py-2 text-left font-bold">Usuário executante</th>
                <th className="px-3 py-2 text-left font-bold">Ação</th>
                <th className="px-3 py-2 text-left font-bold">Mensagem</th>
                <th className="px-3 py-2 text-left font-bold">Data de execução</th>
                <th className="px-3 py-2 text-right font-bold">Alterações</th>
              </tr>
            </thead>
            <tbody>
              {carregando && lista.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-faint">
                    Carregando histórico...
                  </td>
                </tr>
              )}
              {/* Duas frases diferentes: "este registro nunca teve historico" e
                  "sua busca nao achou" sao situacoes distintas, e a segunda tem
                  saida - por isso o botao de limpar vem junto. */}
              {!carregando && visiveis.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-faint">
                    {busca.trim() ? (
                      <>
                        Nenhum registro para “{busca.trim()}”.{' '}
                        <button type="button" onClick={() => setBusca('')} className="font-bold underline">
                          limpar pesquisa
                        </button>
                      </>
                    ) : (
                      'Nenhum histórico encontrado.'
                    )}
                  </td>
                </tr>
              )}
              {visiveis.map((linha) => {
                const quantas = linha.campoDataLista?.length || 0;
                return (
                  <tr key={linha.codigo} className="border-t border-line align-top hover:bg-soft">
                    <td className="px-3 py-1.5">
                      {/* `usuarioExecutante` vem por relation e pode ser nulo em
                          registros gravados por rotina do sistema; o codigo antigo
                          acessava `.nome` direto e quebrava a tabela toda. */}
                      <span className="font-semibold">{linha.usuarioExecutante?.nome || 'sistema'}</span>
                      {linha.usuarioExecutante?.email && (
                        <span className="block text-[11px] text-faint">{linha.usuarioExecutante.email}</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <span
                        className={`inline-flex h-[20px] items-center rounded px-1.5 text-[10px] font-bold tracking-wide ${tomAcao(
                          linha.acao
                        )}`}
                      >
                        {linha.acao || '—'}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">{linha.mensagem}</td>
                    <td className="px-3 py-1.5">
                      <Num className="text-[11px]">{fDateSimple(linha.dataExecucao)}</Num>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {/* Botao desabilitado quando nao ha diff: na versao antiga o
                          icone aparecia sempre e abria um modal vazio. */}
                      <button
                        type="button"
                        disabled={quantas === 0}
                        onClick={() => {
                          setCampoDataLista(linha.campoDataLista || []);
                          setAberto(true);
                        }}
                        className="rounded-md border border-line px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-30"
                        title={quantas === 0 ? 'Este registro não tem campos alterados' : 'Ver antes e depois'}
                      >
                        {quantas === 0 ? '—' : `ver ${quantas}`}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {ordenada.length > linhasPorPagina && (
        <div className="mt-2 flex items-center justify-end gap-2 text-[11px] text-muted-foreground">
          <span>
            página <Num>{paginaAtual + 1}</Num> de <Num>{totalPaginas}</Num> · <Num>{ordenada.length}</Num> itens
          </span>
          <button
            type="button"
            onClick={() => setPagina(0)}
            disabled={paginaAtual === 0}
            className="h-7 w-7 rounded-md border border-line disabled:opacity-30"
          >
            «
          </button>
          <button
            type="button"
            onClick={() => setPagina(paginaAtual - 1)}
            disabled={paginaAtual === 0}
            className="h-7 w-7 rounded-md border border-line disabled:opacity-30"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setPagina(paginaAtual + 1)}
            disabled={paginaAtual >= totalPaginas - 1}
            className="h-7 w-7 rounded-md border border-line disabled:opacity-30"
          >
            ›
          </button>
          <button
            type="button"
            onClick={() => setPagina(totalPaginas - 1)}
            disabled={paginaAtual >= totalPaginas - 1}
            className="h-7 w-7 rounded-md border border-line disabled:opacity-30"
          >
            »
          </button>
        </div>
      )}

      <ModalHistorico
        campoDataLista={campoDataLista}
        handleClickOpen={() => setAberto(true)}
        handleClose={() => setAberto(false)}
        open={aberto}
        setOpen={setAberto}
      />
    </div>
  );
}

function seguroJson(texto?: string): Array<HistoricoCampoDataProps> {
  if (!texto) return [];
  try {
    const dados = JSON.parse(texto);
    return Array.isArray(dados) ? dados : [];
  } catch {
    return [];
  }
}
